import type { FC } from "react";
import React from "react";
import type { QuestionnaireField } from "src/state/QuestionnaireStepCubit/QuestionnaireStepCubit";
import type { ConditionNode } from "src/ui/components/CustomQuestionnairePreview/logicParser";
import {
  ConditionToken,
  TokenTooltip
} from "src/ui/components/CustomQuestionnairePreview/debugPanel.styles";
import styled from "@emotion/styled";

interface ConditionDisplayProps {
  condition: ConditionNode;
  fields: QuestionnaireField[];
  customFormVariables: Record<string, unknown>;
  customFormData: Record<string, unknown>;
  hiddenFields: Record<string, unknown>;
}

const ConditionLine = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  padding: 2px 0;

  &[data-match="true"] {
    background: color-mix(in srgb, var(--debug-success) 10%, transparent);
    border-radius: 3px;
    padding: 2px 4px;
    margin: 1px -4px;
  }

  &[data-match="false"] {
    opacity: 0.6;
  }
`;

const ConditionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-left: 12px;
  border-left: 2px solid var(--debug-border);
  margin: 4px 0;
`;

const OperatorLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: var(--debug-text-muted);
  background: var(--debug-bg-lighter);
  padding: 1px 6px;
  border-radius: 3px;
  margin: 4px 0;
`;

const getTokenType = (
  text: string
): "field" | "variable" | "hidden" | "value" | "text" => {
  if (text.startsWith("@")) {
    return "field";
  }
  if (text.startsWith("$")) {
    return "variable";
  }
  if (text.startsWith("#")) {
    return "hidden";
  }
  if (text.startsWith('"')) {
    return "value";
  }
  return "text";
};

const TokenWithTooltip: FC<{
  text: string;
  tooltip?: string;
  matches?: boolean;
}> = ({ text, tooltip, matches }) => {
  const type = getTokenType(text);
  return (
    <ConditionToken data-type={type} data-match={matches}>
      {text}
      {tooltip && <TokenTooltip>{tooltip}</TokenTooltip>}
    </ConditionToken>
  );
};

const ConditionDisplay: FC<ConditionDisplayProps> = ({
  condition,
  fields,
  customFormVariables,
  customFormData,
  hiddenFields
}) => {
  const resolveTooltip = (text: string): string | undefined => {
    if (text.startsWith("@")) {
      const fieldTitle = text.slice(1).replace("...", "");
      const field = fields.find(
        (f) => f.title === text.slice(1) || f.title?.startsWith(fieldTitle)
      );
      if (!field) {
        return "Field not found";
      }

      const value = customFormData[field.id];
      if (value === undefined || value === null) {
        return "Answer: (no answer yet)";
      }

      if (Array.isArray(value)) {
        const labels = value.map((v) => {
          const choice = field.properties?.choices?.find(
            (c: { id?: string; label?: string }) => c.id === v
          );
          return choice?.label ?? String(v);
        });
        return `Answer: ${labels.join(", ")}`;
      }

      if (typeof value === "object") {
        return `Answer: ${JSON.stringify(value)}`;
      }

      const choice = field.properties?.choices?.find(
        (c: { id?: string; label?: string }) => c.id === String(value)
      );
      return `Answer: ${choice?.label ?? String(value)}`;
    }

    if (text.startsWith("$")) {
      const varName = text.slice(1);
      const value = customFormVariables[varName];
      return `Value: ${value !== undefined ? String(value) : "undefined"}`;
    }

    if (text.startsWith("#")) {
      const hiddenName = text.slice(1);
      const value = hiddenFields[hiddenName] ?? customFormVariables[hiddenName];
      return `Value: ${value !== undefined ? String(value) : "undefined"}`;
    }

    return undefined;
  };

  const renderNode = (node: ConditionNode, depth = 0): React.ReactNode => {
    if (node.type === "comparison") {
      if (node.left && node.op && node.right) {
        return (
          <ConditionLine data-match={node.matches}>
            <TokenWithTooltip
              text={node.left}
              tooltip={resolveTooltip(node.left)}
              matches={node.matches}
            />
            <ConditionToken data-type="text">{node.op}</ConditionToken>
            <TokenWithTooltip
              text={node.right}
              tooltip={resolveTooltip(node.right)}
              matches={node.matches}
            />
          </ConditionLine>
        );
      }
      return (
        <ConditionLine data-match={node.matches}>
          <TokenWithTooltip
            text={node.text ?? ""}
            tooltip={resolveTooltip(node.text ?? "")}
            matches={node.matches}
          />
        </ConditionLine>
      );
    }

    if (node.type === "group" && node.children) {
      if (node.children.length === 1) {
        return renderNode(node.children[0], depth);
      }

      return (
        <ConditionGroup>
          {node.children.map((child, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <OperatorLabel>{node.operator}</OperatorLabel>}
              {renderNode(child, depth + 1)}
            </React.Fragment>
          ))}
        </ConditionGroup>
      );
    }

    return null;
  };

  return <div>{renderNode(condition)}</div>;
};

export default ConditionDisplay;
